import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { Shell } from './Shell';
import { Toast } from '../common';
import { AddUserModal, OnboardingModal, UploadTrainingDocModal, ViewRestaurantModal, AddRestaurantModal, WorkflowReviewModal } from '../modals';
import { MyEarningsPage, AdminFinancialPage, KPIMetricsPage, DashboardPage, ContactsPage, ArchivePage, SettingsPage, AITrainingPage, DocumentsPage, WrongInfoReviewPage } from '../../pages';
import { WaitingForApprovalPage, LearningPage } from '../../pages';
import TrainingKPIPage from '../../pages/TrainingKPIPage';
import GoSnelMapsPage from '../../pages/GoSnelMapsPage';

/**
 * Authenticated Layout Component
 * Renders the Shell + page content based on URL path
 * URL structure: /:role/:page (e.g., /admin/contacts, /marketer/wallet)
 */
export function AuthenticatedLayout() {
  const { role } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ctx = useContext(AppContext);

  if (!ctx || !ctx.currentUser) {
    return <Navigate to="/login" replace />;
  }

  const {
    currentUser,
    modal,
    closeModal,
    toast,
    logout,
    users,
    restaurants,
    userCommissions,
    allCommissions,
    loadingCommissions,
    markCommissionPaid,
    bulkMarkCommissionsPaid,
    showToast,
    loadUsers,
    loadRestaurants,
    updateRestaurant,
    settings,
    setModal,
  } = ctx;

  // Verify user role matches URL
  if (currentUser.role !== role) {
    return <Navigate to={`/${currentUser.role}/dashboard`} replace />;
  }

  // Extract sub-path (e.g., '/admin/dashboard' -> 'dashboard')
  const pathParts = location.pathname.split('/').filter(Boolean);
  const page = pathParts[1] || 'dashboard'; // Default to dashboard

  // Check if user is approved to work (except admins)
  // Unapproved users (trainees) can access learning, documents, and dashboard
  if (currentUser.role !== 'admin' && !currentUser.is_approved_to_work) {
    if (page === 'learning' || page === 'documents' || page === 'dashboard') {
      // Allow access to learning page, shared documents, and dashboard
    } else {
      return <WaitingForApprovalPage currentUser={currentUser} />;
    }
  }

  // Build setPage function that updates URL
  const setPage = (newPage) => {
    navigate(`/${role}/${newPage}`);
  };

  // Handle logout - redirect to login
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <Shell 
        currentUser={currentUser}
        page={page}
        setPage={setPage}
        onLogout={handleLogout}
      >
        {/* Page Routing */}
        {page === 'dashboard' && (
          <DashboardPage 
            currentUser={currentUser}
            restaurants={restaurants}
            users={users}
            userCommissions={userCommissions}
            allCommissions={allCommissions}
          />
        )}

        {page === 'contacts' && <ContactsPage />}

        {page === 'my-contacts' && <ContactsPage viewMode="my-contacts" />}
        
        {page === 'maps' && <GoSnelMapsPage />}
        
        {page === 'archive' && <ArchivePage />}

        {page === 'wallet' && (
          <MyEarningsPage 
            userCommissions={userCommissions}
            loadingCommissions={loadingCommissions}
            currentUser={currentUser}
          />
        )}

        {page === 'financial' && (
          <AdminFinancialPage 
            allCommissions={allCommissions}
            loadingCommissions={loadingCommissions}
            markCommissionPaid={markCommissionPaid}
            bulkMarkCommissionsPaid={bulkMarkCommissionsPaid}
          />
        )}

        {page === 'kpi' && (
          <KPIMetricsPage />
        )}

        {page === 'wrong-info-review' && (
          <WrongInfoReviewPage />
        )}

        {page === 'ai-training' && <AITrainingPage />}

        {page === 'documents' && <DocumentsPage />}

        {page === 'learning' && <LearningPage />}

        {page === 'training-kpi' && <TrainingKPIPage />}

        {page === 'settings' && <SettingsPage />}

        {/* Modal Routing */}
        {modal?.type === 'upload_training_doc' && (
          <UploadTrainingDocModal
            isOpen={true}
            onClose={closeModal}
            currentUser={currentUser}
            showToast={showToast}
          />
        )}

        {modal?.type === 'add_user' && (
          <AddUserModal 
            isOpen={true}
            onClose={closeModal}
            showToast={showToast}
            onUserCreated={() => loadUsers()}
          />
        )}

        {modal?.type === 'add_restaurant' && (
          <AddRestaurantModal 
            isOpen={true}
            onClose={closeModal}
            showToast={showToast}
            currentUser={currentUser}
            onRestaurantAdded={() => loadRestaurants()}
          />
        )}

        {modal?.type === 'onboarding' && (
          <OnboardingModal 
            restaurant={modal.data}
            isOpen={true}
            onClose={closeModal}
            showToast={showToast}
            currentUser={currentUser}
            onUpdateSuccess={() => {
              // Reload restaurants after save
              loadRestaurants();
            }}
          />
        )}

        {modal?.type === 'view_details' && (
          <ViewRestaurantModal 
            isOpen={true}
            onClose={closeModal}
            restaurant={modal.data}
            users={users}
            currentUser={currentUser}
            showToast={showToast}
            settings={settings}
            updateRestaurant={updateRestaurant}
            setModal={setModal}
            onUpdateSuccess={() => {
              // Reload restaurants
              loadRestaurants();
            }}
          />
        )}

        {/* Workflow Review Modal - handles dataentry_action, manager_action, accountant_action, rep_action */}
        {['dataentry_action', 'manager_action', 'accountant_action', 'rep_action'].includes(modal?.type) && (
          <WorkflowReviewModal
            isOpen={true}
            onClose={closeModal}
            restaurant={modal.data}
            currentUser={currentUser}
            showToast={showToast}
            onUpdateSuccess={() => {
              loadRestaurants();
            }}
          />
        )}
      </Shell>

      {/* Toast Notification */}
      {toast && <Toast msg={toast.msg} type={toast.type} />}
    </>
  );
}
